import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Radar, Mail, ShieldCheck, Zap, Heart } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

function Shell({ title, children }) {
  return (
    <div className="min-h-screen bg-[#080B11] text-slate-300">
      <header className="border-b border-[#232D42] px-4 sm:px-6 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg bg-[#00E5A3] flex items-center justify-center"><Radar size={18} className="text-[#04140F]" /></div>
          <span className="font-heading font-extrabold text-lg text-slate-100">Sideline<span className="text-[#00E5A3]">IQ</span></span>
        </Link>
        <Link to="/" className="inline-flex items-center gap-1 text-sm text-slate-400 hover:text-slate-100"><ArrowLeft size={14} /> Home</Link>
      </header>
      <div className="max-w-[820px] mx-auto px-4 sm:px-6 py-10">{children}</div>
    </div>
  );
}

export function About() {
  return (
    <Shell>
      <h1 className="font-heading text-3xl sm:text-4xl font-extrabold text-slate-50 mb-3">Built to keep you informed — not to gouge you.</h1>
      <p className="text-base text-slate-400 leading-relaxed mb-8">
        SidelineIQ is a research tool for people who want the truth about player availability before the
        rest of the world catches up. Injuries, starting goalies, late scratches, transactions, recent form —
        every fact carries its source and a timestamp. No hot takes, no picks, no fluff.
      </p>
      <div className="grid sm:grid-cols-3 gap-4 mb-10">
        {[
          { icon: ShieldCheck, t: "Real data for everyone", d: "Free users get real information too — not a fake demo. Paid tiers only change how fast you see it and how deep it goes." },
          { icon: Zap, t: "Sourced & timestamped", d: "Every item links to where it came from (ESPN and more), marked official vs reported, so you decide what to trust." },
          { icon: Heart, t: "Fair by design", d: "The free tier is genuinely useful. The paid tiers are for speed and insider depth — never a paywall on the basics." },
        ].map((c, i) => (
          <div key={i} className="rounded-xl border border-[#232D42] bg-[#0F1420] p-4">
            <c.icon size={20} className="text-[#00E5A3] mb-2" />
            <div className="font-heading font-bold text-slate-100 mb-1">{c.t}</div>
            <p className="text-sm text-slate-400 leading-relaxed">{c.d}</p>
          </div>
        ))}
      </div>
      <h2 className="font-heading text-xl font-bold text-slate-100 mb-2">How it works</h2>
      <p className="text-sm text-slate-400 leading-relaxed mb-6">
        We continuously pull official injury reports, rosters, schedules, starting goalies, news and odds for the
        NFL, NBA, NHL and MLB, normalize them, and surface what actually matters — with the receipts. Follow players
        and teams, build watchlists, and get pinged the moment something changes.
      </p>
      <p className="text-sm text-slate-400 leading-relaxed">
        Got a source we're missing or an idea to make this better? I read every message — use the{" "}
        <Link to="/contact" className="text-[#00E5A3] hover:underline">contact page</Link> or the feedback button in the corner.
      </p>
      <p className="text-[11px] text-slate-600 mt-10 border-t border-[#232D42] pt-6">
        Research only — not betting advice. 18+, or 21+ where required for sports wagering. Gambling problem? Call 1-800-GAMBLER.
      </p>
    </Shell>
  );
}

export function Contact() {
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);

  const send = async () => {
    if (message.trim().length < 3) { toast.error("Please add a little more detail."); return; }
    setSending(true);
    try {
      await api.post("/feedback", { message, email });
      toast.success("Got it — thank you! I read every note personally.");
      setMessage(""); setEmail("");
    } catch { toast.error("Couldn't send just now. Please try again."); }
    setSending(false);
  };

  return (
    <Shell>
      <h1 className="font-heading text-3xl font-extrabold text-slate-50 mb-2">Get in touch</h1>
      <p className="text-base text-slate-400 leading-relaxed mb-8">
        Questions, missing sources, bugs, or ideas — send them over. I'm building SidelineIQ to keep people
        informed, and your input genuinely shapes what gets built next.
      </p>
      <div className="rounded-xl border border-[#232D42] bg-[#0F1420] p-5 mb-6">
        <div className="flex items-center gap-2 text-slate-300 mb-4"><Mail size={16} className="text-[#00E5A3]" /> support@sidelineiq.io</div>
        <label className="block text-xs font-mono uppercase tracking-wider text-slate-500 mb-1">Your email (optional)</label>
        <input data-testid="contact-email" value={email} onChange={(e) => setEmail(e.target.value)}
          placeholder="you@email.com"
          className="w-full mb-4 bg-[#151B2B] border border-[#232D42] rounded-lg px-3 py-2 text-sm text-slate-200 outline-none focus:border-[#00E5A3]/50" />
        <label className="block text-xs font-mono uppercase tracking-wider text-slate-500 mb-1">Message</label>
        <textarea data-testid="contact-message" value={message} onChange={(e) => setMessage(e.target.value)}
          rows={5} placeholder="What's on your mind? A source I'm missing, a bug, an idea…"
          className="w-full mb-4 bg-[#151B2B] border border-[#232D42] rounded-lg px-3 py-2 text-sm text-slate-200 outline-none focus:border-[#00E5A3]/50 resize-none" />
        <button data-testid="contact-send" onClick={send} disabled={sending}
          className="h-10 px-5 rounded-lg bg-[#00E5A3] hover:bg-[#00C98E] text-[#04140F] font-semibold disabled:opacity-60">
          {sending ? "Sending…" : "Send message"}
        </button>
      </div>
      <p className="text-[11px] text-slate-600">Research only — not betting advice. Gambling problem? Call 1-800-GAMBLER.</p>
    </Shell>
  );
}
